import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Empty, Space, Spin, Table, Tabs, Tag, Tooltip, message } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  MessageOutlined,
  RollbackOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import ProjectPageShell, { useProjectContext } from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import {
  DailyReview,
  ReviewFlag,
  dailyApprovalApis,
  normaliseReview,
} from "../../../api/DailyApprovals";
import {
  ApprovalStatusPill,
  RemarksModal,
  fmtDay,
  fmtMoney,
  fmtWhen,
  perUnitLabel,
} from "./approvalUi";
import "../execution/execution.css";
import "./dailyApprovals.css";

// One site day, reviewed: what needs attention first, then Civil, Material-
// Stores and Mechanical exactly as captured, the review notes, and the history.

type Section = "cost" | "civil" | "material" | "mechanical" | "stock" | "notes";

// A flag names the SECTION it came from, which is not always the tab that shows
// it: the Mucking/PRW heads are reviewed on the cost abstract, not a tab of
// their own. One map rather than a second field on every flag.
const FLAG_TAB: Record<ReviewFlag["section"], Section> = {
  civil: "civil",
  material: "material",
  mechanical: "mechanical",
  stock: "stock",
  norm: "cost",
};
type Transition = "approve" | "reject" | "reopen" | "recall";
type NoteTarget = { section: string; refType: string; refId: number; refLabel: string };

const num = (value: string) => (value === "" || value === undefined ? "—" : value);

const DailyReportReviewPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId: routeProjectId, date = "" } = useParams();
  const context = useProjectContext();
  const projectId = routeProjectId || context.projectId;

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))));
  }, [dispatch, location]);

  const [review, setReview] = useState<DailyReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [section, setSection] = useState<Section>("cost");
  const [focus, setFocus] = useState("");
  const [transition, setTransition] = useState<Transition | null>(null);
  const [noteTarget, setNoteTarget] = useState<NoteTarget | null>(null);
  const [absBusy, setAbsBusy] = useState(false);
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  // The ABS workbook is a fold of the cost abstract already on screen — no
  // second fetch, so the file and the tab can never disagree.
  const downloadAbs = async () => {
    if (!review?.cost.length) return;
    setAbsBusy(true);
    try {
      const helpers = await import("../reports/shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportAbsWorkbook } = await import("./absWorkbook");
      await exportAbsWorkbook({ review, orgName, logo });
      message.success("Cost abstract exported");
    } catch (err) {
      console.error("Error exporting the daily cost abstract:", err);
      message.error("Could not build the workbook");
    } finally {
      setAbsBusy(false);
    }
  };

  const load = useCallback(() => {
    if (!projectId || !date) return;
    setLoading(true);
    dailyApprovalApis
      .review(projectId, date)
      .then((res) => {
        if (res?.status !== true) {
          setError(res?.message || "Could not load the day");
          return;
        }
        setError("");
        setReview(normaliseReview(res.data));
      })
      .finally(() => setLoading(false));
  }, [projectId, date]);

  useEffect(() => {
    load();
  }, [load]);

  const state = review?.state;
  const jumpTo = (flag: ReviewFlag) => {
    setSection(FLAG_TAB[flag.section] ?? "cost");
    setFocus(flag.anchor);
    window.setTimeout(() => {
      document.getElementById(flag.anchor)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const runTransition = async (kind: Transition, remarks: string) => {
    if (!state) return false;
    const body = { dprId: state.dprId, round: state.round, level: state.currentLevel, remarks };
    const res =
      kind === "approve" ? await dailyApprovalApis.approve(body)
        : kind === "reject" ? await dailyApprovalApis.reject(body)
          : kind === "reopen" ? await dailyApprovalApis.reopen(body)
            : await dailyApprovalApis.recall(body);
    if (res?.status !== true) {
      message.error(res?.message || "The action could not be completed");
      load();
      return false;
    }
    message.success(res.data?.message || "Done");
    load();
    return true;
  };

  const saveNote = async (body: any) => {
    const res = await dailyApprovalApis.saveNote(body);
    if (res?.status !== true) {
      message.error(res?.message || "Could not save the note");
      return false;
    }
    message.success(res.data?.message || "Saved");
    load();
    return true;
  };

  const noteButton = (target: NoteTarget) =>
    state?.canNote ? (
      <Tooltip title="Add a review note on this line">
        <Button size="small" type="text" icon={<MessageOutlined />} onClick={() => setNoteTarget(target)} />
      </Tooltip>
    ) : null;

  const rowClass = (record: any) =>
    [record.flag ? `pms-appr-row-flag-${record.flag}` : "", record.anchor === focus ? "pms-appr-row-focus" : ""].join(" ");
  const rowProps = (record: any) => ({ id: record.anchor });

  const openNotes = review?.notes.filter((note) => !note.resolved).length ?? 0;
  const modalCopy: Record<Transition, { title: string; okText: string; min: number; danger?: boolean; placeholder: string; intro: string }> = {
    approve: {
      title: `Approve ${fmtDay(date)}`,
      okText: state && state.currentLevel < state.levelCount ? `Approve level ${state.currentLevel}` : "Approve",
      min: 0,
      placeholder: "Comment (optional)",
      intro: state && state.currentLevel < state.levelCount
        ? `The day moves to the level ${state.currentLevel + 1} approvers.`
        : "The day is approved and stays locked.",
    },
    reject: {
      title: `Reject ${fmtDay(date)}`,
      okText: "Reject",
      min: 15,
      danger: true,
      placeholder: "Tell the site what to correct",
      intro: "The day unlocks for correction. Your remarks and any open notes are shown to the site on Daily Execution.",
    },
    reopen: {
      title: `Reopen ${fmtDay(date)}`,
      okText: "Reopen",
      min: 10,
      danger: true,
      placeholder: "Why does the approved day need to change?",
      intro: "The day unlocks so it can be corrected, then it goes through approval again.",
    },
    recall: {
      title: `Recall ${fmtDay(date)}`,
      okText: "Recall",
      min: 5,
      placeholder: "Why are you recalling the day?",
      intro: "The day unlocks so it can be corrected, and nobody reviews it until it is submitted again.",
    },
  };

  // ------------------------------------------------------------- sections

  const civilTab = (
    <>
      {!review?.civil.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Civil execution captured for this day" />}
      {review?.civil.map((entry: any) => (
        <div className="pms-appr-block" key={entry.entryId} id={`civil-activity-${entry.activityId}`}>
          <div className="pms-appr-block-head">
            <span>
              <b>{entry.activityName}</b>
              {entry.phaseName && <span className="pms-appr-sub"> · {entry.phaseName}</span>}
            </span>
            <span>
              {entry.shiftName || "Whole day"}
              {entry.startTime && entry.endTime ? ` · ${entry.startTime}–${entry.endTime}` : ""}
              {entry.workHours ? ` · ${entry.workHours}` : ""}
            </span>
          </div>
          <div className="pms-appr-chips">
            {entry.slots.map((slot: any) =>
              slot.stretches.length ? (
                slot.stretches.map((stretch: any, i: number) => (
                  <Tag key={`${slot.slotKey}-${i}`}>
                    {slot.slotLabel} · CH {stretch.fromChainage}–{stretch.toChainage} ({stretch.length} m)
                  </Tag>
                ))
              ) : (
                <Tag key={slot.slotKey}>
                  {slot.slotLabel} · {slot.qty} {slot.uomName}
                </Tag>
              )
            )}
            {entry.remarks && <span className="pms-appr-sub">“{entry.remarks}”</span>}
          </div>
          <div style={{ padding: "8px 0 0" }}>
            <Table
              size="small"
              rowKey="anchor"
              pagination={false}
              dataSource={entry.lines}
              rowClassName={rowClass}
              onRow={rowProps}
              scroll={{ x: 900 }}
              columns={[
                {
                  title: "BOQ line",
                  key: "line",
                  render: (_: any, line: any) => (
                    <span>
                      <b>{line.taskSequenceId}</b> {line.taskName}
                      {line.isMonitored && <Tag color="blue" style={{ marginInlineStart: 6 }}>Tracked</Tag>}
                    </span>
                  ),
                },
                { title: "Unit", dataIndex: "uomName", width: 110 },
                { title: "Target", dataIndex: "target", width: 110, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{num(v)}</span> },
                {
                  title: "Actual",
                  key: "actual",
                  width: 130,
                  align: "right" as const,
                  render: (_: any, line: any) => (
                    <span className="pms-appr-num">
                      {num(line.actual)}
                      <div className="pms-appr-sub">
                        {line.measured !== "" ? "measured" : "derived"}
                        {line.stretchActuals.length ? ` · ${line.stretchActuals.length} stretch${line.stretchActuals.length === 1 ? "" : "es"}` : ""}
                      </div>
                    </span>
                  ),
                },
                {
                  title: "Variance",
                  dataIndex: "variancePercent",
                  width: 100,
                  align: "right" as const,
                  render: (v: string) =>
                    v === "" ? <span className="pms-appr-sub">—</span> : (
                      <Tag color={Math.abs(Number(v)) > 10 ? "red" : "green"}>{Number(v) > 0 ? "+" : ""}{v}%</Tag>
                    ),
                },
                { title: "Remarks", dataIndex: "remarks", width: 220, ellipsis: true },
                {
                  title: "",
                  key: "note",
                  width: 48,
                  render: (_: any, line: any) =>
                    noteButton({ section: "civil", refType: "entryLine", refId: line.taskId, refLabel: `${entry.activityName} · ${line.taskSequenceId}` }),
                },
              ]}
            />
          </div>
        </div>
      ))}
    </>
  );

  // The day as the client's ABS cost abstract: one block per task, settled into
  // Material (by category) / Machinery / Out Source — Mucking / PRW, with
  // Target and Actual side by side. This is the view the client reads a day in,
  // so it opens first; the capture-shaped tabs stay as the drill-down, and the
  // flags still jump into them.
  //
  // Rendered as flat rows with a group header rather than nested tables: a
  // nested Table per sub-group puts a second header above every three lines and
  // the columns stop lining up down the page, which is the one thing a cost
  // abstract has to do.
  const costRow = (line: any, depth: number) => (
    <tr key={line.anchor} id={line.anchor} className={line.anchor === focus ? "pms-appr-row-focus" : ""}>
      <td className="pms-abs-label" style={{ paddingLeft: 8 + depth * 14 }}>
        {line.label}
        {line.subLabel && <div className="pms-appr-sub">{line.subLabel}</div>}
      </td>
      <td>{line.uomName}</td>
      <td className="pms-abs-num">{num(line.targetRate)}</td>
      <td className="pms-abs-num">{num(line.targetQty)}</td>
      <td className="pms-abs-num">{fmtMoney(line.targetAmount)}</td>
      <td className={`pms-abs-num ${line.flag ? "pms-appr-bad" : ""}`}>{num(line.actualRate)}</td>
      <td className="pms-abs-num">{num(line.actualQty)}</td>
      <td className="pms-abs-num">{fmtMoney(line.actualAmount)}</td>
      <td className="pms-abs-num">
        {line.variancePercent === "" ? (
          <span className="pms-appr-sub">—</span>
        ) : (
          <Tag color={Math.abs(Number(line.variancePercent)) > 10 ? "red" : "green"}>
            {Number(line.variancePercent) > 0 ? "+" : ""}
            {line.variancePercent}%
          </Tag>
        )}
      </td>
      <td>
        {noteButton({
          section: line.refType === "norm" ? "norm" : line.refType === "machine" ? "mechanical" : "material",
          refType: line.refType,
          refId: line.refId,
          refLabel: line.label,
        })}
      </td>
    </tr>
  );

  const costTotalsRow = (label: string, totals: any, className: string, depth = 0) => (
    <tr key={`${label}-${className}`} className={className}>
      <td className="pms-abs-label" style={{ paddingLeft: 8 + depth * 14 }} colSpan={2}>
        {label}
      </td>
      <td colSpan={2} />
      <td className="pms-abs-num">{fmtMoney(totals.targetAmount)}</td>
      <td colSpan={2} />
      <td className="pms-abs-num">{fmtMoney(totals.actualAmount)}</td>
      <td className="pms-abs-num">
        {totals.variancePercent === "" ? (
          <span className="pms-appr-sub">—</span>
        ) : (
          <b>
            {Number(totals.variancePercent) > 0 ? "+" : ""}
            {totals.variancePercent}%
          </b>
        )}
      </td>
      <td />
    </tr>
  );

  const costTab = (
    <>
      {!review?.cost.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nothing costed on this day — no material, machine, mucking or PRW against any task"
        />
      )}
      {!!review?.cost.length && (
        <>
          <div className="pms-appr-block-head">
            <span>
              <b>Cost abstract</b>
              <div className="pms-appr-sub">
                {review.summary.costTaskCount} BOQ item(s) · target vs actual by cost head
              </div>
            </span>
            <span className="pms-appr-num">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={absBusy}
                onClick={downloadAbs}
                style={{ marginRight: 12 }}
              >
                Download ABS
              </Button>
              Target {fmtMoney(review.summary.costTargetAmount)} · Actual{" "}
              {fmtMoney(review.summary.costActualAmount)}
              {review.summary.costVariancePercent !== "" && (
                <Tag
                  color={Math.abs(Number(review.summary.costVariancePercent)) > 10 ? "red" : "green"}
                  style={{ marginLeft: 8 }}
                >
                  {Number(review.summary.costVariancePercent) > 0 ? "+" : ""}
                  {review.summary.costVariancePercent}%
                </Tag>
              )}
            </span>
          </div>
          {review.cost.map((task) => (
            <div className="pms-appr-block" key={task.taskId}>
              <div className="pms-appr-block-head">
                <span>
                  <b>{task.taskSequenceId}</b> {task.taskName}
                  <div className="pms-appr-sub">{task.activityName}</div>
                </span>
                <span className="pms-appr-num">
                  Produced {num(task.producedQty)} {task.workUomName}
                  {task.totals.actualPerUnit !== "" &&
                    ` · ${fmtMoney(task.totals.actualPerUnit)} / ${task.workUomName || "unit"}`}
                </span>
              </div>
              <table className="pms-abs-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>Head</th>
                    <th rowSpan={2}>UOM</th>
                    <th colSpan={3}>Target</th>
                    <th colSpan={3}>Actual</th>
                    <th rowSpan={2}>Var</th>
                    <th rowSpan={2} />
                  </tr>
                  <tr>
                    <th className="pms-abs-num">Rate</th>
                    <th className="pms-abs-num">Qty</th>
                    <th className="pms-abs-num">Amount</th>
                    <th className="pms-abs-num">Rate</th>
                    <th className="pms-abs-num">Qty</th>
                    <th className="pms-abs-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {task.groups.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="pms-abs-group">
                        <td colSpan={10}>{group.label}</td>
                      </tr>
                      {group.subGroups.map((sub) => (
                        <React.Fragment key={sub.key}>
                          <tr className="pms-abs-sub">
                            <td colSpan={10} style={{ paddingLeft: 22 }}>
                              {sub.label}
                            </td>
                          </tr>
                          {sub.lines.map((line) => costRow(line, 2))}
                          {sub.lines.length > 1 && costTotalsRow(`${sub.label} total`, sub.totals, "pms-abs-subtotal", 2)}
                        </React.Fragment>
                      ))}
                      {group.lines.map((line) => costRow(line, 1))}
                      {costTotalsRow(`${group.label} total`, group.totals, "pms-abs-grouptotal", 1)}
                    </React.Fragment>
                  ))}
                  {costTotalsRow("Task total", task.totals, "pms-abs-tasktotal")}
                  {task.totals.targetPerUnit !== "" && (
                    <tr className="pms-abs-perunit">
                      <td colSpan={2} className="pms-abs-label">
                        Cost per {task.workUomName || "unit"}
                      </td>
                      <td colSpan={2} />
                      <td className="pms-abs-num">{fmtMoney(task.totals.targetPerUnit)}</td>
                      <td colSpan={2} />
                      <td className="pms-abs-num">{fmtMoney(task.totals.actualPerUnit)}</td>
                      <td colSpan={2} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
          {review.summary.costPerUnitLabel === "" && review.cost.length > 1 && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="No day-level cost per unit"
              description="The BOQ items on this day are measured in different units, so their produced quantities cannot be added. Each item's own cost per unit is shown in its block."
            />
          )}
        </>
      )}
    </>
  );

  // The project store's day. A sibling of the task sections, not one of them:
  // daily_material_stock is project-scoped, and its issue value is the same
  // material leaving the store that the task issues already account for — so it
  // is never added into the day's material cost.
  const stockTab = (
    <>
      {!review?.stock.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No stock movement on this day" />
      )}
      {!!review?.stock.length && (
        <div className="pms-appr-block">
          <div className="pms-appr-block-head">
            <span>
              <b>Project store</b>
              <div className="pms-appr-sub">Materials that moved or carry a balance on this day</div>
            </span>
            <span className="pms-appr-num">
              {review.summary.stockMaterialCount} material(s) · closing {fmtMoney(review.summary.stockClosingValue)}
            </span>
          </div>
          <Table
            size="small"
            rowKey="anchor"
            pagination={false}
            dataSource={review.stock.map((row) => ({ ...row, anchor: `stock-${row.materialId}` }))}
            rowClassName={rowClass}
            onRow={rowProps}
            scroll={{ x: 1100 }}
            columns={[
              {
                title: "Material",
                key: "material",
                render: (_: any, row: any) => (
                  <span>
                    {row.productCode && <Tag>{row.productCode}</Tag>}
                    {row.materialName}
                    <div className="pms-appr-sub">{row.categoryName}</div>
                  </span>
                ),
              },
              { title: "UOM", dataIndex: "uomName", width: 80 },
              { title: "Opening", dataIndex: "openingQty", width: 90, align: "right" as const, render: num },
              {
                title: "Purchased",
                key: "purchased",
                width: 130,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span>
                    {num(row.purchasedQty)}
                    {row.purchasedRate !== "" && (
                      <div className={row.purchaseAboveNorm ? "pms-appr-bad" : "pms-appr-sub"}>
                        @ {row.purchasedRate}
                        {row.purchaseAboveNorm && row.normRate !== "" ? ` · norm ${row.normRate}` : ""}
                      </div>
                    )}
                  </span>
                ),
              },
              {
                title: "Issued",
                key: "issued",
                width: 130,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span>
                    <span className={row.targetState === "OVER" ? "pms-appr-bad" : ""}>{num(row.issuedQty)}</span>
                    {row.targetState === "OVER" && row.targetQty !== "" && (
                      <div className="pms-appr-bad">allocated {row.targetQty}</div>
                    )}
                  </span>
                ),
              },
              {
                title: "Tasks booked",
                key: "tasks",
                width: 140,
                align: "right" as const,
                render: (_: any, row: any) => {
                  if (row.varianceState === "NO_TASK_ISSUE") return <span className="pms-appr-sub">—</span>;
                  if (row.varianceState === "UNIT_MISMATCH")
                    return <Tag color="orange">different unit</Tag>;
                  const off = row.varianceState === "STORE_HIGHER" || row.varianceState === "STORE_LOWER";
                  return (
                    <span>
                      <span className={off ? "pms-appr-bad" : ""}>{num(row.taskIssuedQty)}</span>
                      {off && row.varianceQty !== "" && <div className="pms-appr-bad">{row.varianceQty}</div>}
                    </span>
                  );
                },
              },
              {
                title: "Closing",
                key: "closing",
                width: 130,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span>
                    <span className={Number(row.closingQty) < 0 ? "pms-appr-bad" : ""}>{num(row.closingQty)}</span>
                    <div className="pms-appr-sub">{fmtMoney(row.closingValue)}</div>
                  </span>
                ),
              },
              { title: "Avg rate", dataIndex: "avgRate", width: 95, align: "right" as const, render: num },
              { title: "Remarks", dataIndex: "remarks", width: 180, ellipsis: true },
              {
                title: "",
                key: "note",
                width: 48,
                render: (_: any, row: any) =>
                  noteButton({ section: "stock", refType: "material", refId: row.materialId, refLabel: row.materialName }),
              },
            ]}
          />
        </div>
      )}
    </>
  );

  const materialTab = (
    <>
      {!review?.materials.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No material issued on this day" />}
      {review?.materials.map((task: any) => (
        <div className="pms-appr-block" key={task.taskId}>
          <div className="pms-appr-block-head">
            <span>
              <b>{task.taskSequenceId}</b> {task.taskName}
              <div className="pms-appr-sub">{task.activityName}</div>
            </span>
            <span className="pms-appr-num">
              Produced {num(task.producedQty)} {task.workUomName} · {fmtMoney(task.totalAmount)}
              {task.perUnit ? ` · ${fmtMoney(task.perUnit)} / ${perUnitLabel(task.workUomName)}` : ""}
            </span>
          </div>
          <Table
            size="small"
            rowKey="anchor"
            pagination={false}
            dataSource={task.lines}
            rowClassName={rowClass}
            onRow={rowProps}
            scroll={{ x: 900 }}
            columns={[
              {
                title: "Material",
                key: "material",
                render: (_: any, line: any) => (
                  <span>
                    {line.productCode && <Tag>{line.productCode}</Tag>}
                    {line.materialName}
                  </span>
                ),
              },
              { title: "Qty", dataIndex: "qty", width: 90, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{num(v)}</span> },
              { title: "UOM", dataIndex: "uomName", width: 100 },
              { title: "Rate", dataIndex: "rate", width: 110, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{v ? fmtMoney(v) : "—"}</span> },
              { title: "Amount", dataIndex: "amount", width: 120, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{fmtMoney(v)}</span> },
              {
                title: `₹ / unit`,
                dataIndex: "perUnit",
                width: 110,
                align: "right" as const,
                render: (v: string) => <span className="pms-appr-num">{v ? fmtMoney(v) : "—"}</span>,
              },
              {
                title: "Issued till date / allotted",
                key: "till",
                width: 170,
                align: "right" as const,
                render: (_: any, line: any) => <span className="pms-appr-num">{num(line.tillDate)} / {num(line.allotted)}</span>,
              },
              { title: "Remarks", dataIndex: "remarks", width: 180, ellipsis: true },
              {
                title: "",
                key: "note",
                width: 48,
                render: (_: any, line: any) =>
                  noteButton({ section: "material", refType: "material", refId: line.materialId, refLabel: `${task.taskSequenceId} · ${line.materialName}` }),
              },
            ]}
          />
        </div>
      ))}
    </>
  );

  const mechanicalTab = (
    <>
      {!review?.mechanical.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No machine hours logged on this day" />}
      {review?.mechanical.map((task: any) => (
        <div className="pms-appr-block" key={task.taskId}>
          <div className="pms-appr-block-head">
            <span>
              <b>{task.taskSequenceId}</b> {task.taskName}
              <div className="pms-appr-sub">{task.activityName}</div>
            </span>
            <span className="pms-appr-num">
              Produced {num(task.producedQty)} {task.workUomName} · {fmtMoney(task.totalCost)}
              {task.perUnit ? ` · ${fmtMoney(task.perUnit)} / ${perUnitLabel(task.workUomName)}` : ""}
            </span>
          </div>
          <Table
            size="small"
            rowKey="anchor"
            pagination={false}
            dataSource={task.machines}
            rowClassName={rowClass}
            onRow={rowProps}
            scroll={{ x: 1000 }}
            columns={[
              {
                title: "Machine",
                key: "machine",
                render: (_: any, m: any) => (
                  <span>
                    <Tag>{m.assetCode}</Tag>
                    {m.assetName}
                  </span>
                ),
              },
              {
                title: "Hours",
                key: "hours",
                width: 110,
                align: "right" as const,
                render: (_: any, m: any) => (
                  <span className="pms-appr-num">
                    {num(m.hours)}
                    {m.targetHours && <div className="pms-appr-sub">target {Number(m.targetHours).toFixed(2)}</div>}
                  </span>
                ),
              },
              { title: "Owning", dataIndex: "owningCost", width: 120, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{v ? fmtMoney(v) : "No rate card"}</span> },
              {
                title: "Operational",
                key: "operational",
                width: 220,
                align: "right" as const,
                render: (_: any, m: any) => (
                  <span className="pms-appr-num">
                    {fmtMoney(m.operationalCost)}
                    {m.heads.length > 0 && (
                      <div className="pms-appr-sub">
                        {m.heads.map((h: any) => `${h.label} ${h.qty || "—"} ${h.uomName} × ${h.rate ? fmtMoney(h.rate) : "—"}`).join(" · ")}
                      </div>
                    )}
                  </span>
                ),
              },
              { title: "Total", dataIndex: "totalCost", width: 120, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{fmtMoney(v)}</span> },
              { title: "₹ / unit", dataIndex: "perUnit", width: 110, align: "right" as const, render: (v: string) => <span className="pms-appr-num">{v ? fmtMoney(v) : "—"}</span> },
              { title: "Idle reason", dataIndex: "idleReason", width: 160, ellipsis: true },
              {
                title: "",
                key: "note",
                width: 48,
                render: (_: any, m: any) =>
                  noteButton({ section: "mechanical", refType: "machine", refId: m.assetId, refLabel: `${task.taskSequenceId} · ${m.assetCode} ${m.assetName}` }),
              },
            ]}
          />
        </div>
      ))}
    </>
  );

  const notesTab = (
    <>
      {state?.canNote && (
        <Button icon={<MessageOutlined />} style={{ marginBottom: 12 }} onClick={() => setNoteTarget({ section: "general", refType: "", refId: 0, refLabel: "The day" })}>
          Add a note on the day
        </Button>
      )}
      {!review?.notes.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No review notes" />}
      {review?.notes.map((note) => (
        <div key={note.noteId} className={`pms-appr-note ${note.resolved ? "resolved" : ""}`}>
          <Space size={6} wrap>
            <Tag color={note.resolved ? "default" : "gold"}>{note.resolved ? "Resolved" : "Open"}</Tag>
            {note.refLabel && <Tag>{note.refLabel}</Tag>}
            <span className="pms-appr-sub">Round {note.round}</span>
          </Space>
          <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{note.note}</div>
          <div className="meta">
            {note.createdByName} · {fmtWhen(note.createdAt)}
            {note.resolved ? ` · resolved by ${note.resolvedByName} ${fmtWhen(note.resolvedAt)}` : ""}
          </div>
          <Space size={4} style={{ marginTop: 6 }}>
            <Button size="small" onClick={() => saveNote({ dprId: state!.dprId, op: note.resolved ? "reopen" : "resolve", noteId: note.noteId })}>
              {note.resolved ? "Reopen" : "Resolve"}
            </Button>
            {state?.canNote && (
              <Button size="small" type="text" danger onClick={() => saveNote({ dprId: state!.dprId, op: "delete", noteId: note.noteId })}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      ))}
    </>
  );

  // ----------------------------------------------------------------- page

  const actions = state && (
    <Space wrap>
      {state.canRecall && (
        <Button icon={<RollbackOutlined />} onClick={() => setTransition("recall")}>
          Recall
        </Button>
      )}
      {state.canReopen && (
        <Button icon={<UnlockOutlined />} onClick={() => setTransition("reopen")}>
          Reopen
        </Button>
      )}
      {state.canReject && (
        <Button danger icon={<CloseOutlined />} onClick={() => setTransition("reject")}>
          Reject
        </Button>
      )}
      {state.canApprove && (
        <Button type="primary" icon={<CheckOutlined />} onClick={() => setTransition("approve")}>
          {state.currentLevel < state.levelCount ? `Approve level ${state.currentLevel}` : "Approve"}
        </Button>
      )}
    </Space>
  );

  return (
    <>
      <ProjectPageShell
        headTitle={`Daily report · ${fmtDay(date)}`}
        breadcrumb={[
          { label: review?.projectName || context.projectName, onClick: () => navigate(-2) },
          { label: "Daily Approvals", onClick: () => navigate(`/pms/project/daily-approvals/${projectId}`, { state: location.state }) },
          { label: fmtDay(date) },
        ]}
        eyebrow="Daily Approvals"
        title={
          <span>
            {fmtDay(date)} {state && <ApprovalStatusPill state={state} />}
          </span>
        }
        onBack={() => navigate(-1)}
        actions={actions}
        loading={loading && !review}
        stats={
          review && (
            <>
              <StatCard label="Faces" value={review.summary.faceCount} />
              <StatCard label="BOQ lines" value={review.summary.boqLineCount} />
              <StatCard label="Material" value={fmtMoney(review.summary.materialAmount)} />
              <StatCard label="Machinery" value={fmtMoney(review.summary.machineryAmount)} />
              <StatCard label="Flags" value={review.summary.flagCount} tone={review.summary.flagCount ? "onhold" : "completed"} />
            </>
          )
        }
      >
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        {!review ? (
          <div style={{ textAlign: "center", padding: 48 }}>{loading ? <Spin /> : null}</div>
        ) : (
          <>
            {state && !state.status && (
              <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Not submitted yet" description="This is what a reviewer will see once the day is submitted." />
            )}
            {state && state.status === "Submitted" && state.levelCount === 0 && (
              <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Submitted and locked" description="No approvers are set up for this project, so nobody reviews it. Set approvers on the Daily Approvals page." />
            )}
            {state && ["Rejected", "Recalled", "Reopened"].includes(state.status) && (
              <Alert
                type={state.status === "Rejected" ? "error" : "warning"}
                showIcon
                style={{ marginBottom: 16 }}
                message={`${state.status} by ${state.lastActionByName || "—"} · ${fmtWhen(state.lastActionAt)}`}
                description={state.lastRemarks || undefined}
              />
            )}
            <div className="pms-appr-layout">
              <div className="pms-appr-rail">
                <div className="pms-appr-panel">
                  <div className="pms-appr-panel-head">
                    Needs attention <Tag color={review.flags.length ? "gold" : "green"}>{review.flags.length}</Tag>
                  </div>
                  <div className="pms-appr-panel-body">
                    {!review.flags.length && <div className="pms-appr-empty">Nothing flagged on this day.</div>}
                    {review.flags.map((flag, i) => (
                      <button key={i} type="button" className={`pms-appr-flag ${flag.severity}`} onClick={() => jumpTo(flag)}>
                        <b>{flag.title}</b>
                        {flag.detail}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pms-appr-panel">
                  <div className="pms-appr-panel-head">History</div>
                  <div className="pms-appr-panel-body">
                    {!review.history.length && <div className="pms-appr-empty">Not submitted yet.</div>}
                    <ul className="pms-appr-history">
                      {review.history
                        .slice()
                        .reverse()
                        .map((action) => (
                          <li key={action.actionId} className={action.action}>
                            <b>{action.action}</b>
                            {action.level ? ` at level ${action.level}` : ""} · {action.userName}
                            {action.isAdmin ? " (admin)" : ""}
                            <div className="when">
                              Round {action.round} · {fmtWhen(action.createdAt)}
                            </div>
                            {action.remarks && <div className="remarks">{action.remarks}</div>}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <Tabs
                  className="pms-tabs"
                  activeKey={section}
                  onChange={(key) => setSection(key as Section)}
                  items={[
                    { key: "cost", label: `Cost abstract (${review.summary.costTaskCount})`, children: costTab },
                    { key: "civil", label: `Civil (${review.civil.length})`, children: civilTab },
                    { key: "material", label: `Material-Stores (${review.materials.length})`, children: materialTab },
                    { key: "mechanical", label: `Mechanical (${review.summary.machineCount})`, children: mechanicalTab },
                    { key: "stock", label: `Store-Stock (${review.summary.stockMaterialCount})`, children: stockTab },
                    { key: "notes", label: `Notes (${openNotes} open)`, children: notesTab },
                  ]}
                />
              </div>
            </div>
          </>
        )}
      </ProjectPageShell>

      {transition && (
        <RemarksModal
          open
          title={modalCopy[transition].title}
          intro={modalCopy[transition].intro}
          okText={modalCopy[transition].okText}
          danger={modalCopy[transition].danger}
          minLength={modalCopy[transition].min}
          placeholder={modalCopy[transition].placeholder}
          onCancel={() => setTransition(null)}
          onSubmit={(remarks) => runTransition(transition, remarks)}
        />
      )}
      {noteTarget && state && (
        <RemarksModal
          open
          title={`Note on ${noteTarget.refLabel}`}
          okText="Add note"
          minLength={3}
          placeholder="What should the site check or correct?"
          onCancel={() => setNoteTarget(null)}
          onSubmit={(note) =>
            saveNote({
              dprId: state.dprId,
              op: "add",
              section: noteTarget.section,
              refType: noteTarget.refType,
              refId: noteTarget.refId,
              refLabel: noteTarget.refLabel,
              note,
            })
          }
        />
      )}
    </>
  );
};

export default DailyReportReviewPage;
